// ─────────────────────────────────────────────────────────────────────────────
// engine.ts — Single-stroke spell engine
//
// Orchestrates the three layers:
//
//   stroke points  →  detect (shape_detector)
//                  →  match  (MatcherChain)
//                  →  recipe (executes RegisteredSpell.recipe)
//                  →  GrimoireActions out
//
// The engine itself dispatches nothing — it returns an action list. The
// caller (CanvasApp) decides how to dispatch (with origin tagging, undo
// grouping, etc.).
//
// Cast mode shuts as soon as the engine returns a non-empty action list
// (the spell fired). If the engine returns [], cast mode behaviour is
// the caller's choice (probably "cancel cast, drop the stroke").
// ─────────────────────────────────────────────────────────────────────────────

import type { GrimoireAction, ActionOrigin } from '@shared/grimoire_action';
import type { PageId, Point, RegionBounds } from '@shared/notebook_model';
import type { WvSelection } from '../notebook_state';
import { detectShape, type DetectInput, type StrokeShape } from './detection/shape_detector';
import { MatcherChain, type MatchContext, type SpellMatch } from './matcher';
import type { RegisteredSpell, RecipeContext, SpellRecipe } from './recipe';

export interface EngineContext {
  pageId: PageId | null;
  selection: WvSelection;
  origin: ActionOrigin;
  /** Geometry queries supplied by the caller. */
  blockAtPoint(p: Point): string | null;
  blocksInRegion(region: RegionBounds): string[];
  strokesInRegion(region: RegionBounds): string[];
  strokesCrossingLine(from: Point, to: Point): string[];
  /** Recipe-side helper: cascade lookup from strokes to owning ink blocks. */
  findInkBlocksByStrokes(strokeIds: ReadonlyArray<string>): string[];
}

export interface EngineResult {
  shape: StrokeShape;
  match: SpellMatch | null;
  actions: GrimoireAction[];
  spellId: string | null;
}

export class SpellEngine {
  private readonly _spells = new Map<string, RegisteredSpell>();
  private readonly _matchers = new MatcherChain();

  /** Register a spell — its matcher + its recipe. The matcher's spellId
   *  must equal the registered spell.id; we enforce that here so a typo
   *  in a matcher's spellId surfaces immediately. */
  register<P = Record<string, unknown>>(
    spell: RegisteredSpell<P>,
    matcher: (shape: StrokeShape, ctx: MatchContext) => SpellMatch<P> | null,
  ): void {
    if (this._spells.has(spell.id)) {
      throw new Error(`[spell] duplicate spell id: ${spell.id}`);
    }
    this._spells.set(spell.id, spell as unknown as RegisteredSpell);
    this._matchers.register(spell.id, (shape, ctx) => {
      const m = matcher(shape, ctx);
      if (!m) return null;
      if (m.spellId !== spell.id) {
        console.warn(`[spell] matcher returned wrong id ${m.spellId} for spell ${spell.id}`);
        return null;
      }
      return m as SpellMatch;
    });
  }

  /** Single-stroke evaluation entry point.  Detects the shape, asks the
   *  matcher chain, and (if matched) runs the recipe. */
  evaluate(stroke: DetectInput, ctx: EngineContext): EngineResult {
    const shape = detectShape(stroke);
    const matchCtx: MatchContext = {
      pageId: ctx.pageId,
      selection: ctx.selection,
      blockAtPoint: ctx.blockAtPoint,
      blocksInRegion: ctx.blocksInRegion,
      strokesInRegion: ctx.strokesInRegion,
      strokesCrossingLine: ctx.strokesCrossingLine,
    };
    const match = this._matchers.match(shape, matchCtx);
    if (!match) {
      return { shape, match: null, actions: [], spellId: null };
    }
    const spell = this._spells.get(match.spellId);
    if (!spell) {
      console.warn(`[spell] matched unknown spell id: ${match.spellId}`);
      return { shape, match, actions: [], spellId: null };
    }
    const recipeCtx: RecipeContext = {
      pageId: ctx.pageId,
      selection: ctx.selection,
      now: Date.now(),
      origin: ctx.origin,
      findInkBlocksByStrokes: ctx.findInkBlocksByStrokes,
    };
    const recipe = spell.recipe as SpellRecipe<Record<string, unknown>>;
    let actions: GrimoireAction[] = [];
    try {
      actions = recipe(match.params, recipeCtx);
    } catch (err) {
      console.error(`[spell] recipe ${match.spellId} threw`, err);
    }
    return { shape, match, actions, spellId: match.spellId };
  }

  /** List for the debug surface — registered spell ids in registration order. */
  list(): RegisteredSpell[] {
    return Array.from(this._spells.values());
  }

  /** Manually invoke a recipe with a synthesised match. The matcher
   *  chain is bypassed entirely — used by the debug menu to run any
   *  spell against the *current* selection. Returns the action list. */
  invokeManually(
    spellId: string,
    params: Record<string, unknown>,
    ctx: EngineContext,
  ): GrimoireAction[] {
    const spell = this._spells.get(spellId);
    if (!spell) {
      console.warn(`[spell] invokeManually: unknown spell id ${spellId}`);
      return [];
    }
    const recipeCtx: RecipeContext = {
      pageId: ctx.pageId,
      selection: ctx.selection,
      now: Date.now(),
      origin: ctx.origin,
      findInkBlocksByStrokes: ctx.findInkBlocksByStrokes,
    };
    const recipe = spell.recipe as SpellRecipe<Record<string, unknown>>;
    try {
      return recipe(params, recipeCtx);
    } catch (err) {
      console.error(`[spell] manual recipe ${spellId} threw`, err);
      return [];
    }
  }
}
